import { Edit, Star } from "@mui/icons-material/";
import { Autocomplete, Chip, IconButton, TextField } from "@mui/material";
import { DataGridPro, GridOverlay, GridToolbarContainer, GridToolbarFilterButton } from "@mui/x-data-grid-pro";
import React, { useImperativeHandle, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import history from "../../../Utils/history";
import { LoadingSpinner } from "../../Viewer/Components/LoadingSpinner";
import { avatar } from "./helpers";
import { useGetUsers } from "./useGetUsers";

export function UserTable() {
  const rows = useSelector((state) => state.admin.rows);
  const loading = useSelector((state) => state.admin.loading);
  const total = useSelector((state) => state.admin.total);
  const roles = useSelector((state) => state.roles.roles);
  const [roleFilters, setRoleFilters] = useState([]);
  useGetUsers({ roleFilters });

  const columns = useMemo(
    () => [
      {
        field: "Avatar",
        flex: 1,
        renderCell: (params) => params.value,
        filterable: false,
      },
      {
        field: "Username",
        flex: 2,
        renderCell: (params) => {
          const row = rows.filter((row) => row.user_account.username === params.value);

          return row[0].is_org_admin ? (
            <>
              <Star />
              {params.value}
            </>
          ) : (
            params.value
          );
        },
      },
      { field: "Full Name", flex: 2 },
      { field: "Email", flex: 2 },
      {
        field: "Roles",
        flex: 2,
        renderCell: (params) => {
          return params.value;
        },
        valueGetter: (params) => {
          return params.value.map((x) => x.name).join(", ");
        },

        filterOperators: [
          {
            label: "Includes",
            value: "includes",
            getApplyFilterFn: (filterItem) => {
              if (
                !filterItem.columnField ||
                !filterItem.value ||
                !filterItem.value.length ||
                !filterItem.operatorValue
              ) {
                return null;
              }

              return (params) => {
                const rolesInColumn = params.value.split(",").map((x) => x.trim());
                const rolesToFilter = filterItem.value;

                const isInRoles = rolesToFilter.reduce((a, c) => {
                  if (rolesInColumn.includes(c)) {
                    a = true;
                  }
                  return a;
                }, false);
                return isInRoles;
              };
            },
            InputComponent: (props) => {
              const { item, applyValue, focusElementRef } = props;
              const rolesRef = React.useRef(null);
              useImperativeHandle(focusElementRef, () => ({
                focus: () => {
                  rolesRef.current.focus();
                },
              }));

              const handleFilterChange = (event, newValue) => {
                applyValue({ ...item, value: newValue });
              };
              //https://github.com/mui/mui-x/issues/3075, Multiselect within DataGrid cell

              return (
                <Autocomplete
                  ref={rolesRef}
                  multiple
                  value={item?.value ? [...item.value] : []}
                  id="tags-outlined"
                  options={roles.map((role) => role.name)}
                  getOptionLabel={(option) => option}
                  filterSelectedOptions
                  onChange={handleFilterChange}
                  renderInput={(params) => <TextField {...params} label="Roles to Filter" placeholder="Roles" />}
                />
              );
            },
            InputComponentProps: { type: "singleSelect", valueOptions: roles.map((role) => role.name) },
          },
        ],
      },
      {
        field: "Edit",
        flex: 1,
        renderCell: (params) => params.value,
        filterable: false,
      },
    ],
    [rows, roles]
  );
  const tableRows = rows.map((row) => {
    return {
      id: row.id,
      Avatar: avatar(row.user_account),
      Username: row.user_account.username,
      "Full Name": row.user_account.full_name,
      Email: row.user_account.email,
      Roles: row.roles,
      Edit: (
        <IconButton onClick={() => history.push(`/admin/users/${row.id}`)}>
          <Edit />
        </IconButton>
      ),
    };
  });
  return (
    <div style={{ padding: 0, height: "100%" }}>
      <DataGridPro
        style={{ height: "100%", overflowY: "auto" }}
        components={{
          Toolbar: () => (
            <GridToolbarContainer>
              <GridToolbarFilterButton />
              {roles
                .filter((role) => role?.list_filterable)
                .map((role) => {
                  return (
                    <Chip
                      key={role.id}
                      label={role.name}
                      component="a"
                      variant={"outlined"}
                      clickable
                      style={{ backgroundColor: roleFilters.includes(role.id) ? "#9e9e9e63" : "transparent" }}
                      onClick={() =>
                        setRoleFilters((state) => {
                          return state.includes(role.id)
                            ? [...state.filter((r) => r !== role.id)]
                            : [...state, role.id];
                        })
                      }
                    />
                  );
                })}
            </GridToolbarContainer>
          ),
          NoRowsOverlay: () => (
            <GridOverlay style={{ flexDirection: "column" }}>
              {loading ? <LoadingSpinner /> : "No Results Found"}
            </GridOverlay>
          ),
          Footer: () => (
            <div style={{ margin: "0px 16px", display: "flex", alignItems: "center" }}>
              Active Users: {total ? total : 0}
            </div>
          ),
        }}
        columns={columns}
        rows={tableRows}
      />
    </div>
  );
}
